/**
 * The signed-in person's own profile row (`profiles`) — what /profile shows and edits about the
 * PERSON. Their organization is read and changed through `@/lib/organizations` (org-first profile,
 * DEC-061).
 *
 * Retired here (D-3, D-6): `label`, `bio`, `city` and `country` are neither read nor written — the
 * company is the organization, and the about/city/countries are the organization's. Photos are not
 * written here either: a crop writes them at once through the `update_profile_image` RPC.
 */
import { supabase } from "./supabase";
import { UserCacheService } from "./user-cache";
import { isEmailShape } from "./corporate-hierarchy";

// Post-signup profile enrichment
// The handle_new_user() trigger already creates the profile with basic fields
// (display_name, role, email) from auth metadata.
// This function is a hook for any post-signup work that the trigger can't do.
// TODO: Once onboarding page is built, this will link organization_id to profile.
export async function createUserProfile(
  userId: string,
  _email: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Trigger handles profile creation. Nothing additional needed for now.
    // Future: onboarding page will handle org linking, phone, position, etc.
    return { success: true };
  } catch (error) {
    return { success: false, error: "Unexpected error creating profile" };
  }
}

/** The person's own profile as /profile holds it. Text fields read "" when not set. */
export interface OwnProfile {
  name: string;
  position: string;
  /** The Work Contact Email (`profiles.contact_email`) — never an identity; sign-in is `profiles.email`. */
  contact_email: string;
  phone: string;
  profile_picture: string;
  header_image: string;
  /** The account type (`profiles.role`). */
  role: string | null;
  /**
   * The account's organization (`profiles.organization_id`) and its position in it (`org_role`), as
   * this read found them. The page's "no organization" comes from here: the capability resolver reads
   * a failed lookup as "none", which must never be told to the person as a fact.
   */
  organization_id: string | null;
  org_role: string | null;
}

/** The four details the person edits on /profile, as the form holds them. */
export type PersonalDetails = Pick<OwnProfile, "name" | "position" | "contact_email" | "phone">;

/**
 * A narrow update of the person's own row, in the database's column names. A key left out is no
 * change. `position` and `phone` clear with `null`; the work contact email cannot be cleared here
 * (a blank one is simply not sent) and the name never can.
 */
export interface OwnProfileUpdate {
  display_name?: string;
  position?: string | null;
  contact_email?: string;
  phone?: string | null;
}

/** The only columns `updateOwnProfile` writes (BUILD-RULES rule 11). */
const OWN_PROFILE_COLUMNS = ["display_name", "position", "contact_email", "phone"] as const;

/** The person's own profile. Throws when the read fails or the account has no profile row. */
export async function getProfile(userId: string): Promise<OwnProfile> {
  const row = await UserCacheService.getProfile(userId);
  if (!row) {
    throw new Error("Profile record not found. Please contact support.");
  }
  return {
    name: row.display_name || "",
    position: row.position || "",
    contact_email: row.contact_email || "",
    phone: row.phone || "",
    profile_picture: row.profile_picture || "",
    header_image: row.header_image || "",
    role: row.role || null,
    organization_id: row.organization_id || null,
    org_role: row.org_role || null,
  };
}

/**
 * What the person changed, as an update body: only fields whose trimmed value differs from the saved
 * one. An emptied position or phone is `null` (clear); an emptied work contact email is left out —
 * never sent as `''` (the form says it can't be emptied).
 */
export function ownProfileChanges(saved: PersonalDetails, edited: PersonalDetails): OwnProfileUpdate {
  const changes: OwnProfileUpdate = {};
  const name = edited.name.trim();
  if (name !== saved.name.trim()) changes.display_name = name;
  const position = edited.position.trim();
  if (position !== saved.position.trim()) changes.position = position || null;
  const contactEmail = edited.contact_email.trim();
  if (contactEmail && contactEmail !== saved.contact_email.trim()) changes.contact_email = contactEmail;
  const phone = edited.phone.trim();
  if (phone !== saved.phone.trim()) changes.phone = phone || null;
  return changes;
}

export type UpdateOwnProfileResult = { ok: true } | { ok: false; error: string };

/**
 * Write the person's own details: `.update()` of exactly the given keys (any other key is dropped)
 * on their row, never an upsert — the `handle_new_user` trigger guarantees the row. A malformed work
 * contact email or an empty name is refused before anything is sent. An update that wrote no row is
 * a failure. It shows no toast: the caller says what happened, once.
 */
export async function updateOwnProfile(userId: string, updates: OwnProfileUpdate): Promise<UpdateOwnProfileResult> {
  const body: OwnProfileUpdate = {};
  for (const column of OWN_PROFILE_COLUMNS) {
    if (updates[column] !== undefined) Object.assign(body, { [column]: updates[column] });
  }
  if (Object.keys(body).length === 0) return { ok: true };
  if (body.contact_email !== undefined && !isEmailShape(body.contact_email)) {
    return { ok: false, error: "Enter one email address, like name@company.com." };
  }
  if (body.display_name !== undefined && !body.display_name.trim()) {
    return { ok: false, error: "Your name can't be empty." };
  }
  try {
    const { error } = await supabase
      .from("profiles")
      .update(body)
      .eq("user_id", userId)
      .select("user_id")
      .single();
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "Couldn't save your profile" };
  }
}

export async function getUserRole(): Promise<string | null> {
  try {
    // Get authenticated user via cache service (deduplicates concurrent calls)
    const user = await UserCacheService.getUser();
    if (!user) return null;

    // Get role via cache service (deduplicates concurrent calls)
    const role = await UserCacheService.getUserRole(user.id);
    return role;
  } catch (err) {
    console.error("Failed to fetch user role:", err);
    return null; // Return null on error, let guards handle appropriately
  }
}
